import { LitElement, html, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { guardedCustomElement } from '../utils/guarded-custom-element';
import { SDSEventDispatcherMixin, SDSDelegateInteractionMixin } from '../mixins';
import { styles } from './sds-textfield.styles';

import '../sds-alert/sds-alert';
import '../sds-icon/sds-icon';
import { SDS_SIZES } from '../types';

type InputType =
	| 'hidden'
	| 'text'
	| 'search'
	| 'tel'
	| 'url'
	| 'email'
	| 'password'
	| 'datetime'
	| 'date'
	| 'month'
	| 'week'
	| 'time'
	| 'datetime-local'
	| 'number'
	| 'range'
	| 'color'
	| 'checkbox'
	| 'radio'
	| 'file'
	| 'submit'
	| 'image'
	| 'reset'
	| 'button';

type InputSize = Extract<SDS_SIZES, 'lg' | 'md' | 'sm'>;
export type AutocompleteTypes =
	| 'off'
	| 'on'
	| 'name'
	| 'honorific-prefix'
	| 'given-name'
	| 'additional-name'
	| 'family-name'
	| 'honorific-suffix'
	| 'nickname'
	| 'email'
	| 'username'
	| 'new-password'
	| 'current-password'
	| 'one-time-code'
	| 'organization-title'
	| 'organization'
	| 'street-address'
	| 'address-line1'
	| 'address-line2'
	| 'address-line3'
	| 'address-level4'
	| 'address-level3'
	| 'address-level2'
	| 'address-level1'
	| 'country'
	| 'country-name'
	| 'postal-code'
	| 'cc-name'
	| 'cc-given-name'
	| 'cc-additional-name'
	| 'cc-family-name'
	| 'cc-number'
	| 'cc-exp'
	| 'cc-exp-month'
	| 'cc-exp-year'
	| 'cc-csc'
	| 'cc-type'
	| 'transaction-currency'
	| 'transaction-amount'
	| 'language'
	| 'bday'
	| 'bday-day'
	| 'bday-month'
	| 'bday-year'
	| 'sex'
	| 'tel'
	| 'tel-country-code'
	| 'tel-national'
	| 'tel-area-code'
	| 'tel-local'
	| 'tel-extension'
	| 'impp'
	| 'url'
	| 'photo';

export interface SDSTextFieldProps {
	type?: InputType;
	value?: string;
	name?: string;
	label?: string;
	placeholder?: string;
	'help-text'?: string;
	'error-message'?: string;
	disabled?: boolean;
	readonly?: boolean;
	autocomplete?: AutocompleteTypes;
	required?: boolean;
	pattern?: string;
	minlength?: number;
	maxlength?: number;
	size?: InputSize;
}

@guardedCustomElement('sds-textfield')
export class SDSTextField extends SDSDelegateInteractionMixin(SDSEventDispatcherMixin(LitElement)) {
	// Styling
	static override styles = [styles];

	@property({ type: String, reflect: true }) type: SDSTextFieldProps['type'] = 'text';
	@property({ type: String }) value?: SDSTextFieldProps['value'];
	@property({ type: String, reflect: true }) name?: SDSTextFieldProps['name'];
	@property({ type: String, reflect: true }) label?: SDSTextFieldProps['label'];
	@property({ type: String, reflect: true }) placeholder?: SDSTextFieldProps['placeholder'];
	@property({ type: String, attribute: 'help-text', reflect: true }) helpText?: SDSTextFieldProps['help-text'];
	@property({ type: String, attribute: 'error-message', reflect: true }) errorMessage?: SDSTextFieldProps['error-message'];
	@property({ type: Boolean, reflect: true }) disabled: SDSTextFieldProps['disabled'] = false;
	@property({ type: Boolean, attribute: 'readonly', reflect: true }) readOnly: SDSTextFieldProps['readonly'] = false;
	@property({ type: String, reflect: true }) autocomplete?: SDSTextFieldProps['autocomplete'];
	@property({ type: Boolean, reflect: true }) required: SDSTextFieldProps['required'] = false;
	@property({ type: String, reflect: true }) pattern?: SDSTextFieldProps['pattern'];
	@property({ type: Number, reflect: true }) minlength?: SDSTextFieldProps['minlength'];
	@property({ type: Number, reflect: true }) maxlength?: SDSTextFieldProps['maxlength'];
	@property({ type: String, reflect: true }) size: SDSTextFieldProps['size'] = 'md';

	// State
	@state() private hasIcon = false;

	@query('#sds-textfield') control!: HTMLInputElement;

	static formAssociated = true;
	public readonly internals: ElementInternals;

	static override shadowRootOptions: ShadowRootInit = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	};

	private _defaultValue: string | null = null;

	constructor() {
		super();
		this.internals = this.attachInternals();
	}

	override connectedCallback() {
		super.connectedCallback();
		this._defaultValue = this.value ?? null;
		this.internals.setFormValue(this._defaultValue);
	}

	override updated(changedProperties: Map<string | number | symbol, unknown>) {
		super.updated(changedProperties);

		if (changedProperties.has('value')) {
			this.internals.setFormValue(this.value ?? '');

			if (this.control) {
				this.control.value = this.value ?? '';
			}
		}
	}

	private readonly handleSlotChange = (e: Event) => {
		const slot = e.target as HTMLSlotElement;
		this.hasIcon = slot.assignedNodes({ flatten: true }).length > 0;
	};

	private updateValidity() {
		if (this.control.checkValidity()) {
			this.internals.setValidity({});
		} else {
			this.internals.setValidity({ customError: true }, this.control.validationMessage, this.control);
		}
	}

	private readonly _handleInputChange = (event: InputEvent) => {
		event.stopImmediatePropagation();
		if (this.disabled || !(event.target instanceof HTMLInputElement)) {
			return;
		}

		this.value = event.target.value;
		this.internals.setFormValue(this.value === '' ? null : this.value);
		this.updateValidity();
		this.dispatchSheriffEvent(event.type);
	};

	private readonly _handleSelect = (event: Event) => {
		event.stopImmediatePropagation();
		if (!this.disabled) {
			this.dispatchSheriffEvent('select');
		}
	};

	private readonly _handleFocus = (event: FocusEvent) => {
		event.stopImmediatePropagation();
		this.dispatchSheriffEvent('focus');
	};

	private readonly _handleBlur = (event: FocusEvent) => {
		event.stopImmediatePropagation();
		this.updateValidity();
		this.dispatchSheriffEvent('blur');
	};

	private readonly _handleKeyDown = (event: KeyboardEvent) => {
		event.stopImmediatePropagation();

		const shouldContinue = this.dispatchKeyboardEvent('keydown', event);

		if (!shouldContinue) return;

		if (event.key === 'Enter') {
			const form = this.internals?.form;

			if (form?.requestSubmit) {
				// Try to use requestSubmit as this will run form validation before submitting
				// https://developer.mozilla.org/en-US/docs/Web/API/HTMLFormElement/requestSubmit#usage_notes
				form.requestSubmit();
			} else {
				form?.submit();
			}
		}
	};

	formResetCallback() {
		this.internals.setFormValue(this._defaultValue);

		const newValue = this._defaultValue ?? '';
		this.control.value = newValue;
		this.value = newValue;
		this.updateValidity();
	}

	select(): void {
		if (!this.disabled) this.control.select();
	}

	get selectionStart(): number | null {
		return this.control ? this.control.selectionStart : null;
	}

	set selectionStart(value: number | null) {
		if (this.control && value !== null) {
			this.control.selectionStart = value;
		}
	}

	get selectionEnd(): number | null {
		return this.control ? this.control.selectionEnd : null;
	}
	set selectionEnd(value: number | null) {
		if (this.control && value !== null) {
			this.control.selectionEnd = value;
		}
	}

	setSelectionRange(start: number, end: number, direction?: 'forward' | 'backward' | 'none'): void {
		if (this.control) {
			this.control.setSelectionRange(start, end, direction);
		}
	}

	checkValidity(): boolean {
		return this.control.checkValidity();
	}

	reportValidity(): boolean {
		return this.control.reportValidity();
	}

	setCustomValidity(message: string): void {
		this.control.setCustomValidity(message);
		this.updateValidity();
	}

	override render() {
		const textfieldClasses = {
			'sds-textfield': true,
			'sds-textfield--error': Boolean(this.errorMessage),
			'sds-textfield--has-icon': this.hasIcon,
		};

		return html`
			<div class="${classMap(textfieldClasses)}">
				${this.label
					? html`<label for="sds-textfield" class="sds-textfield__label">${this.readOnly ? html`<sds-icon name="lock-material-filled" size="m"></sds-icon>` : nothing}</sds-icon>${this.label}</label>`
					: nothing}
				${this.helpText ? html`<p class="sds-textfield__help-text">${this.helpText}</p>` : nothing}
				<div class="sds-textfield__input-container">
					<input
						part="input"
						class="sds-textfield__input"
						type="${ifDefined(this.type)}"
						id="sds-textfield"
						?disabled="${this.disabled}"
						?readonly="${this.readOnly}"
						placeholder="${ifDefined(this.placeholder)}"
						.value="${this.value ?? ''}"
						name="${ifDefined(this.name)}"
						autocomplete="${ifDefined(this.autocomplete as any)}"
						?required="${this.required}"
						pattern="${ifDefined(this.pattern)}"
						minlength="${ifDefined(this.minlength)}"
						maxlength="${ifDefined(this.maxlength)}"
						@input="${this._handleInputChange}"
						@change="${this._handleInputChange}"
						@select="${this._handleSelect}"
						@focus="${this._handleFocus}"
						@blur="${this._handleBlur}"
						@keydown="${this._handleKeyDown}"
					/>
					<slot name="after" @slotchange="${this.handleSlotChange}" aria-hidden="true"></slot>
				</div>
				${this.errorMessage ? html`<sds-alert variant="secondary" data-palette="danger"><p slot="message">${this.errorMessage}</p></sds-alert>` : nothing}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sds-textfield': SDSTextField;
	}
}
